/**
 * The LEVEL EDITOR's document MODEL — pure, UI-agnostic operations on a {@link LevelDocument}. Every
 * authoring action (place, move, resize, edit a field, delete) is a pure function here that takes a doc
 * and returns a NEW doc, so React state updates are trivial and the SAME operations back both the mouse
 * UI and the `__duckEditor` e2e driver (one code path, server-authoritative-style: the model is the
 * single source of truth). Persistence is a thin localStorage wrapper; the durable path is JSON
 * export/import via the contract's {@link parseLevel}/{@link serializeLevel}.
 */
import {
  officeLevel,
  parseLevel,
  serializeLevel,
  type DecorItem,
  type InteractiveObject,
  type LevelDocument,
  type Seat,
  type SeatKind,
  type WorldDoor,
  type WorldZone,
} from '@duck/contracts';

// ── Selection + placement ─────────────────────────────────────────────────────────────────────────

export type EditorItemType = 'zone' | 'door' | 'object' | 'seat' | 'decor';

/** A reference to one selected element (or null). The id is unique within its collection. */
export interface SelectionRef {
  type: EditorItemType;
  id: string;
}

/** What the palette has armed for the next canvas click (or null = the select/move tool). */
export interface PlacementArm {
  type: 'object' | 'seat' | 'zone' | 'decor';
  /** Object/seat/decor kind to place (ignored for zones). */
  kind?: string;
}

// ── Persistence ───────────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'dd:editor:doc';

/** Load the working document from localStorage, or fall back to a fresh copy of the office. */
export function loadWorkingDoc(): LevelDocument {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return parseLevel(raw);
  } catch {
    /* malformed/old saved doc → start clean from the office */
  }
  return officeLevel();
}

/** Autosave the working document (best-effort; ignores quota / private-mode failures). */
export function saveWorkingDoc(doc: LevelDocument): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, serializeLevel(doc));
  } catch {
    /* noop */
  }
}

/** Forget the autosaved document (used by "Reset to office"). */
export function clearWorkingDoc(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────────────────────────

export function cloneDoc(doc: LevelDocument): LevelDocument {
  return structuredClone(doc);
}

/**
 * Snap a coordinate to the grid (grid ≤ 0 → round to the nearest integer). Re-exported from the SHARED
 * placement controller so the editor and the in-game Furniture Store snap identically (one source — ТЗ §2).
 */
export { snap } from '../office/render/placement-controller';

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Mint the next free `${prefix}-${n}` id not already used in `items`. */
function nextId(prefix: string, items: readonly { id: string }[]): string {
  const ids = new Set(items.map((i) => i.id));
  let n = 1;
  while (ids.has(`${prefix}-${n}`)) n++;
  return `${prefix}-${n}`;
}

/** The world-coordinate centre of a door opening (for hit-testing + markers). */
export function doorCenter(door: WorldDoor, zone: WorldZone): { x: number; y: number } {
  const T = 12; // WALL_THICKNESS — the threshold sits on the room edge
  switch (door.side) {
    case 'N':
      return { x: door.pos, y: zone.y + T / 2 };
    case 'S':
      return { x: door.pos, y: zone.y + zone.height - T / 2 };
    case 'W':
      return { x: zone.x + T / 2, y: door.pos };
    default:
      return { x: zone.x + zone.width - T / 2, y: door.pos };
  }
}

/** Clamp a door's position so its opening stays fully within the zone edge it sits on. */
function clampDoorPos(zone: WorldZone, side: WorldDoor['side'], pos: number, width: number): number {
  const half = width / 2;
  return side === 'N' || side === 'S'
    ? clamp(pos, zone.x + half, zone.x + zone.width - half)
    : clamp(pos, zone.y + half, zone.y + zone.height - half);
}

// ── Catalogs (the EXISTING object/seat kinds the editor composes) ─────────────────────────────────

export interface ObjectCatalogEntry {
  kind: InteractiveObject['kind'];
  label: string;
  icon: string;
}

/** The placeable interactive-object kinds (defaults mirror the shipped office props). */
export const OBJECT_CATALOG: readonly ObjectCatalogEntry[] = [
  { kind: 'coffee', label: 'Coffee machine', icon: '☕' },
  { kind: 'desk', label: 'Workstation', icon: '💻' },
  { kind: 'sofa', label: 'Sofa', icon: '🛋️' },
  { kind: 'tv', label: 'TV', icon: '📺' },
  { kind: 'arcade', label: 'Arcade', icon: '🕹️' },
  { kind: 'jukebox', label: 'Jukebox', icon: '🎵' },
];

/** Default elected effect (presence + one-shot animation) per object kind — matches the office. */
const OBJECT_EFFECT: Record<InteractiveObject['kind'], InteractiveObject['effect']> = {
  coffee: { presence: 'coffee_break', animation: 'coffee' },
  desk: { presence: 'working', animation: 'type' },
  sofa: { presence: 'focus', animation: 'sit' },
  tv: { presence: 'gathering', animation: 'wave' },
  arcade: { presence: 'gathering', animation: 'dance' },
  // The jukebox toggles the room disco (ADR-0031); dance one-shot for flavour.
  jukebox: { animation: 'dance', disco: true },
};

export interface SeatCatalogEntry {
  kind: SeatKind;
  label: string;
  icon: string;
}

/** The placeable seat kinds. */
export const SEAT_CATALOG: readonly SeatCatalogEntry[] = [
  { kind: 'chair', label: 'Chair', icon: '🪑' },
  { kind: 'sofa', label: 'Sofa', icon: '🛋️' },
  { kind: 'meeting-seat', label: 'Meeting seat', icon: '💺' },
  { kind: 'workstation', label: 'Desk', icon: '💻' },
  { kind: 'toilet', label: 'Toilet', icon: '🚽' },
];

// ── Lookups ───────────────────────────────────────────────────────────────────────────────────────

export function findItem(
  doc: LevelDocument,
  sel: SelectionRef | null,
): WorldZone | WorldDoor | InteractiveObject | Seat | DecorItem | null {
  if (!sel) return null;
  switch (sel.type) {
    case 'zone':
      return doc.zones.find((z) => z.id === sel.id) ?? null;
    case 'door':
      return doc.doors.find((d) => d.id === sel.id) ?? null;
    case 'object':
      return doc.objects.find((o) => o.id === sel.id) ?? null;
    case 'seat':
      return doc.seats.find((s) => s.id === sel.id) ?? null;
    case 'decor':
      return doc.decor.find((d) => d.id === sel.id) ?? null;
  }
}

// ── Mutations (each returns a NEW document) ───────────────────────────────────────────────────────

export function addObject(
  doc: LevelDocument,
  kind: InteractiveObject['kind'],
  x: number,
  y: number,
): { doc: LevelDocument; id: string } {
  const next = cloneDoc(doc);
  const proto = OBJECT_CATALOG.find((o) => o.kind === kind)!;
  const id = nextId('obj', next.objects);
  next.objects.push({ id, kind, icon: proto.icon, label: proto.label, x, y, effect: { ...OBJECT_EFFECT[kind] } });
  return { doc: next, id };
}

export function addSeat(
  doc: LevelDocument,
  kind: SeatKind,
  x: number,
  y: number,
): { doc: LevelDocument; id: string } {
  const next = cloneDoc(doc);
  const proto = SEAT_CATALOG.find((s) => s.kind === kind)!;
  const id = nextId('seat', next.seats);
  next.seats.push({ id, kind, icon: proto.icon, label: proto.label, x, y, facing: 0 });
  return { doc: next, id };
}

export function addZone(doc: LevelDocument, x: number, y: number): { doc: LevelDocument; id: string } {
  const next = cloneDoc(doc);
  const id = nextId('room', next.zones);
  const n = next.zones.length + 1;
  next.zones.push({ id, name: `Room ${n}`, x, y, width: 240, height: 160, isolated: false });
  return { doc: next, id };
}

export function addDecor(
  doc: LevelDocument,
  kind: string,
  x: number,
  y: number,
): { doc: LevelDocument; id: string } {
  const next = cloneDoc(doc);
  const id = nextId('decor', next.decor);
  const item: DecorItem = { id, kind, x, y };
  if (kind === 'rug') {
    item.w = 160;
    item.h = 90;
  } else if (kind === 'long-desk') {
    item.w = 84;
  }
  next.decor.push(item);
  return { doc: next, id };
}

/** Add a door (centred) on one edge of a zone. Returns null if the zone is unknown. */
export function addDoor(
  doc: LevelDocument,
  zoneId: string,
  side: WorldDoor['side'],
): { doc: LevelDocument; id: string } | null {
  const zone = doc.zones.find((z) => z.id === zoneId);
  if (!zone) return null;
  const next = cloneDoc(doc);
  const width = 120;
  const pos = side === 'N' || side === 'S' ? zone.x + zone.width / 2 : zone.y + zone.height / 2;
  // Door ids follow the `${zoneId}-${side}` convention; suffix if a same-side door already exists.
  let id = `${zoneId}-${side}`;
  let k = 2;
  const taken = new Set(next.doors.map((d) => d.id));
  while (taken.has(id)) id = `${zoneId}-${side}-${k++}`;
  next.doors.push({ id, zoneId, side, pos: clampDoorPos(zone, side, pos, width), width });
  return { doc: next, id };
}

/** Move the selected element to a world point (zones drag their attached doors along with them). */
export function moveSelected(doc: LevelDocument, sel: SelectionRef, x: number, y: number): LevelDocument {
  const next = cloneDoc(doc);
  switch (sel.type) {
    case 'object': {
      const o = next.objects.find((i) => i.id === sel.id);
      if (o) {
        o.x = x;
        o.y = y;
      }
      break;
    }
    case 'seat': {
      const s = next.seats.find((i) => i.id === sel.id);
      if (s) {
        s.x = x;
        s.y = y;
      }
      break;
    }
    case 'decor': {
      const d = next.decor.find((i) => i.id === sel.id);
      if (d) {
        d.x = x;
        d.y = y;
      }
      break;
    }
    case 'zone': {
      const z = next.zones.find((i) => i.id === sel.id);
      if (z) {
        const dx = x - z.x;
        const dy = y - z.y;
        z.x = x;
        z.y = y;
        // Keep this room's doors attached: N/S doors track the X shift, E/W doors track the Y shift.
        for (const d of next.doors) {
          if (d.zoneId !== z.id) continue;
          d.pos += d.side === 'N' || d.side === 'S' ? dx : dy;
          d.pos = clampDoorPos(z, d.side, d.pos, d.width);
        }
      }
      break;
    }
    case 'door': {
      const d = next.doors.find((i) => i.id === sel.id);
      const z = d ? next.zones.find((zz) => zz.id === d.zoneId) : undefined;
      if (d && z) {
        d.pos = clampDoorPos(z, d.side, d.side === 'N' || d.side === 'S' ? x : y, d.width);
      }
      break;
    }
  }
  return next;
}

/** Resize a zone (min 40×40), re-clamping its doors onto the new edges. */
export function resizeZone(
  doc: LevelDocument,
  id: string,
  x: number,
  y: number,
  width: number,
  height: number,
): LevelDocument {
  const next = cloneDoc(doc);
  const z = next.zones.find((i) => i.id === id);
  if (z) {
    z.x = x;
    z.y = y;
    z.width = Math.max(40, width);
    z.height = Math.max(40, height);
    for (const d of next.doors) {
      if (d.zoneId === z.id) d.pos = clampDoorPos(z, d.side, d.pos, d.width);
    }
  }
  return next;
}

/**
 * Rotate the selected element by `delta` radians around its anchor. Works for every placeable kind:
 * decor + interactive objects use `rotation`; a seat rotates its `facing`. Zones/doors don't rotate.
 */
export function rotateSelected(doc: LevelDocument, sel: SelectionRef, delta: number): LevelDocument {
  const next = cloneDoc(doc);
  if (sel.type === 'decor') {
    const d = next.decor.find((i) => i.id === sel.id);
    if (d) d.rotation = (d.rotation ?? 0) + delta;
  } else if (sel.type === 'object') {
    const o = next.objects.find((i) => i.id === sel.id);
    if (o) o.rotation = (o.rotation ?? 0) + delta;
  } else if (sel.type === 'seat') {
    const s = next.seats.find((i) => i.id === sel.id);
    if (s) s.facing = (s.facing ?? 0) + delta;
  }
  return next;
}

/** Apply an arbitrary field patch to the selected element (the inspector's editor). */
export function updateSelected(
  doc: LevelDocument,
  sel: SelectionRef,
  patch: Record<string, unknown>,
): LevelDocument {
  const next = cloneDoc(doc);
  const item = findItem(next, sel) as Record<string, unknown> | null;
  if (item) Object.assign(item, patch);
  // A door's pos may now hang off its (possibly edited) zone — re-clamp so the inspector can't break it.
  if (sel.type === 'door') {
    const d = next.doors.find((i) => i.id === sel.id);
    const z = d ? next.zones.find((zz) => zz.id === d.zoneId) : undefined;
    if (d && z) d.pos = clampDoorPos(z, d.side, d.pos, d.width);
  }
  return next;
}

/** Duplicate the selected element (offset by dx/dy), returning the new doc + new id, or null. */
export function duplicateSelected(
  doc: LevelDocument,
  sel: SelectionRef,
  dx: number,
  dy: number,
): { doc: LevelDocument; id: string } | null {
  const src = findItem(doc, sel);
  if (!src) return null;
  const next = cloneDoc(doc);
  switch (sel.type) {
    case 'object': {
      const o = src as InteractiveObject;
      const id = nextId('obj', next.objects);
      next.objects.push({ ...o, id, x: o.x + dx, y: o.y + dy, effect: { ...o.effect } });
      return { doc: next, id };
    }
    case 'seat': {
      const s = src as Seat;
      const id = nextId('seat', next.seats);
      next.seats.push({ ...s, id, x: s.x + dx, y: s.y + dy });
      return { doc: next, id };
    }
    case 'decor': {
      const d = src as DecorItem;
      const id = nextId('decor', next.decor);
      next.decor.push({ ...d, id, x: d.x + dx, y: d.y + dy });
      return { doc: next, id };
    }
    case 'zone': {
      const z = src as WorldZone;
      const id = nextId('room', next.zones);
      next.zones.push({ ...z, id, name: `${z.name} copy`, x: z.x + dx, y: z.y + dy });
      return { doc: next, id };
    }
    case 'door': {
      const d = src as WorldDoor;
      const zone = next.zones.find((zz) => zz.id === d.zoneId);
      if (!zone) return null;
      let id = `${d.zoneId}-${d.side}`;
      let k = 2;
      const taken = new Set(next.doors.map((x) => x.id));
      while (taken.has(id)) id = `${d.zoneId}-${d.side}-${k++}`;
      next.doors.push({ ...d, id, pos: clampDoorPos(zone, d.side, d.pos + (dx || dy || 40), d.width) });
      return { doc: next, id };
    }
  }
}

/** Delete the selected element. Deleting a zone also removes the doors that belonged to it. */
export function deleteSelected(doc: LevelDocument, sel: SelectionRef): LevelDocument {
  const next = cloneDoc(doc);
  switch (sel.type) {
    case 'object':
      next.objects = next.objects.filter((o) => o.id !== sel.id);
      break;
    case 'seat':
      next.seats = next.seats.filter((s) => s.id !== sel.id);
      break;
    case 'decor':
      next.decor = next.decor.filter((d) => d.id !== sel.id);
      break;
    case 'door':
      next.doors = next.doors.filter((d) => d.id !== sel.id);
      break;
    case 'zone':
      next.zones = next.zones.filter((z) => z.id !== sel.id);
      next.doors = next.doors.filter((d) => d.zoneId !== sel.id);
      break;
  }
  return next;
}
